import { useEffect, useState } from "react";
import { X, Send, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/lib/SettingsContext";
import { createRequest } from "@/lib/api";
import { track } from "@/lib/analytics";

const TYPES = [
  { key: "consultation", label: "Consultation / rendez-vous" },
  { key: "conference", label: "Inviter en conférence" },
  { key: "presse", label: "Journaliste / média" },
  { key: "partenariat", label: "Partenariat / collaboration" },
  { key: "autre", label: "Autre demande" },
];

const TYPE_TITLE = {
  consultation: "Demander une consultation",
  conference: "Inviter Dorothée en conférence",
  presse: "Demande presse / média",
  partenariat: "Proposer un partenariat",
  autre: "Votre demande",
};

// Champs spécifiques par type : on ne demande que l'utile, pour qualifier dès le 1er contact.
const FIELDS = {
  consultation: [
    { k: "prestation", label: "Prestation souhaitée", type: "select", options: ["Voyance", "Contact défunt", "Onirothérapie", "Numérologie", "Lecture d'âme", "Hypnose", "Je ne sais pas encore"] },
    { k: "format", label: "Format", type: "select", options: ["Présentiel", "À distance", "Écrit", "Peu importe"] },
    { k: "dispo", label: "Vos disponibilités (indicatif)", type: "text" },
  ],
  conference: [
    { k: "organisme", label: "Organisme / structure", type: "text" },
    { k: "ville", label: "Ville", type: "text" },
    { k: "date", label: "Date envisagée", type: "text" },
    { k: "participants", label: "Nombre approx. de participants", type: "text" },
    { k: "theme", label: "Thème souhaité", type: "text" },
  ],
  presse: [
    { k: "media", label: "Média", type: "text" },
    { k: "intervention", label: "Type d'intervention", type: "select", options: ["Interview", "Portrait", "Citation / réaction", "Reportage", "Autre"] },
    { k: "sujet", label: "Sujet", type: "text" },
    { k: "delai", label: "Délai / date de publication", type: "text" },
  ],
  partenariat: [
    { k: "structure", label: "Structure / marque", type: "text" },
    { k: "nature", label: "Nature du partenariat", type: "text" },
  ],
  autre: [],
};

export default function DemandeModal({ open, onClose, presetType = null }) {
  const settings = useSettings();
  const [type, setType] = useState(presetType);
  const [fields, setFields] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [hp, setHp] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setType(presetType);
      setFields({});
      setName(""); setEmail(""); setPhone(""); setMessage("");
      setConsent(false); setHp(""); setDone(false);
      track("demande_start", { type: presetType || "chooser" });
    }
  }, [open, presetType]);

  if (!open) return null;
  const email_to = settings?.appointment_email || "dorothee-medium@hotmail.fr";
  const inp = "w-full bg-[#050814] border border-[rgba(198,167,106,0.3)] px-3 py-2.5 text-[#F3E9D2] text-sm focus:outline-none focus:border-[#C6A76A]";

 const submit = () => {
if (!email.trim() || !email.includes("@")) {
toast.error("Merci d'indiquer une adresse e-mail valide.");
return;
}

if (!consent) {
toast.error("Merci d'accepter le traitement de votre demande.");
return;
}

const details = Object.entries(fields || {})
.filter(([_, value]) => value)
.map(([key, value]) => `${key} : ${value}`)
.join("\n");

const subject = encodeURIComponent(
`Nouvelle demande de rendez-vous - ${name || "Site Dorothée Lancelot"}`
);

const body = encodeURIComponent(
`Nouvelle demande depuis dorotheelancelot.fr

Nom : ${name || ""}
E-mail : ${email}
Téléphone : ${phone || ""}
Type de demande : ${type || ""}

${details}

Message :
${message || ""}`
);

window.location.href = `mailto:${email_to}?subject=${subject}&body=${body}`;
};

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="demande-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-[#0B1020] border border-[rgba(198,167,106,0.35)] dl-bubble max-h-[92vh] overflow-y-auto">
        <div className="p-7 md:p-8">
          <button data-testid="demande-close" onClick={onClose} className="absolute top-4 right-4 text-[#F3E9D2]/60 hover:text-[#F3E9D2]"><X size={22} /></button>

          {done ? (
            <div className="text-center py-6" data-testid="demande-success">
              <CheckCircle2 size={40} className="text-[#C6A76A] mx-auto mb-4" />
              <h2 className="serif text-2xl text-[#F3E9D2]">Demande transmise</h2>
              <p className="text-sm text-[#F3E9D2]/65 mt-3">Merci. Votre demande a été enregistrée et sera traitée dans les meilleurs délais. Vous recevrez une réponse à l'adresse indiquée.</p>
              <button onClick={onClose} className="dl-btn dl-btn-solid mt-7">Fermer</button>
            </div>
          ) : !type ? (
            <>
              <div className="dl-eyebrow mb-2">Un seul point de contact</div>
              <h2 className="serif text-3xl text-[#F3E9D2]">Quelle est votre demande ?</h2>
              <p className="text-sm text-[#F3E9D2]/60 mt-3">Choisissez le motif : nous n'afficherons que les informations utiles à votre demande.</p>
              <div className="mt-6 space-y-2">
                {TYPES.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`demande-type-${t.key}`}
                    onClick={() => setType(t.key)}
                    className="w-full text-left border border-[rgba(198,167,106,0.3)] px-4 py-3.5 text-[#F3E9D2] hover:bg-white/[0.04] hover:border-[#C6A76A] transition-colors"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="dl-eyebrow mb-2">Demande qualifiée</div>
              <h2 className="serif text-2xl md:text-3xl text-[#F3E9D2]">{TYPE_TITLE[type]}</h2>
              {!presetType && (
                <button onClick={() => setType(null)} className="dl-link text-xs mt-2">← Changer de motif</button>
              )}

              <div className="mt-5 space-y-3.5">
                {(FIELDS[type] || []).map((f) => (
                  <div key={f.k}>
                    <label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>{f.label}</label>
                    {f.type === "select" ? (
                      <select data-testid={`demande-field-${f.k}`} className={inp} value={fields[f.k] || ""} onChange={(e) => setFields((c) => ({ ...c, [f.k]: e.target.value }))}>
                        <option value="">—</option>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input data-testid={`demande-field-${f.k}`} className={inp} value={fields[f.k] || ""} onChange={(e) => setFields((c) => ({ ...c, [f.k]: e.target.value }))} />
                    )}
                  </div>
                ))}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                  <div>
                    <label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>Votre nom</label>
                    <input data-testid="demande-name" className={inp} value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div>
                    <label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>Téléphone (facultatif)</label>
                    <input data-testid="demande-phone" className={inp} value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>E-mail *</label>
                  <input data-testid="demande-email" type="email" className={inp} value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div>
                  <label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>Votre message</label>
                  <textarea data-testid="demande-message" rows={3} className={inp} value={message} onChange={(e) => setMessage(e.target.value)} />
                </div>

                {/* Honeypot anti-spam (masqué) */}
                <input tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} aria-hidden="true" />

                <label className="flex items-start gap-2.5 text-xs text-[#F3E9D2]/55 pt-1 cursor-pointer">
                  <input data-testid="demande-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#C6A76A]" />
                  <span>J'accepte que ces informations soient utilisées uniquement pour traiter ma demande. Aucune donnée n'est cédée à un tiers.</span>
                </label>

                <button data-testid="demande-submit" onClick={submit} disabled={sending} className="dl-btn dl-btn-solid w-full justify-center mt-1 disabled:opacity-50">
                  <Send size={15} /> {sending ? "Envoi…" : "Envoyer ma demande"}
                </button>
                <p className="text-center text-[11px] text-[#F3E9D2]/40 pt-1">
                  Vous préférez l'e-mail direct ?{" "}
                  <a href={`mailto:${email_to}`} className="dl-link inline-flex items-center gap-1"><Mail size={11} /> {email_to}</a>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
